import React from 'react';
import { AcvtHomeGame, AcvtSupportedLanguages } from '../../../../types/acvt';
import { usePreview, useT } from '../../../preview';
import style from './style.module.scss';

type SmallGameCard = {
    gameObject: AcvtHomeGame;
    /**
     * Kept for backward compatibility — the active language now comes
     * from `usePreview()` so the card resolves the right localized
     * fields without needing the prop threaded through.
     */
    language?: AcvtSupportedLanguages;
    opened: React.Dispatch<
        React.SetStateAction<{
            homePromoCard: boolean;
            homeGameCard: boolean;
            promoCard: boolean;
            gameCard: boolean;
        }>
    >;
    setSingleGame: (promo: AcvtHomeGame) => void;
};

/**
 * Vertical (small) game card shown in the home page games row.
 * Mirrors the Figma "Games / Blue" component (file
 * HfdrKMPuxRduN2Tx38kLsR / node 3783-293034): tall card with a
 * BU-themed background, title + description on top, circular masked
 * illustration in the bottom half, and a "Play now" pill in the
 * bottom-right corner.
 */
export const AcvtSmallGameCard = ({
    gameObject,
    opened,
    setSingleGame,
}: SmallGameCard) => {
    const t = useT();
    const { language } = usePreview();
    const lang = language as AcvtSupportedLanguages;

    const openDetail = () => {
        setSingleGame(gameObject);
        opened((prevState) => ({ ...prevState, homeGameCard: true }));
    };

    const buttonLabel = gameObject.buttonTitle?.[lang] || t.games.playButton;

    // Per-language colors picked by the CMS user when authoring the
    // game. Card background, title and description follow the user's
    // pick (with brand-blue fallbacks). The CTA pill is always the app
    // primary red so the call to action stays consistent.
    const cardStyle: React.CSSProperties = {
        backgroundColor:
            gameObject.backgroundColor?.[lang] ?? 'var(--Brand-Blue-1)',
    };
    const titleStyle: React.CSSProperties = {
        color: gameObject.titleColor?.[lang] ?? 'var(--Brand-Blue-8)',
    };
    const subtitleStyle: React.CSSProperties = {
        color: gameObject.subtitleColor?.[lang] ?? 'var(--Text-Primary)',
    };
    const ctaStyle: React.CSSProperties = {
        backgroundColor: 'var(--preview-primary)',
        color: 'var(--Neutral-1)',
    };

    return (
        <button
            type="button"
            className={style.card}
            onClick={openDetail}
            style={cardStyle}
        >
            <div className={style.text}>
                <span className={style.title} style={titleStyle}>
                    {gameObject.title?.[lang]}
                </span>
                {gameObject.subTitle?.[lang] && (
                    <span className={style.subtitle} style={subtitleStyle}>
                        {gameObject.subTitle?.[lang]}
                    </span>
                )}
            </div>
            <div className={style.image_mask}>
                <img
                    alt=""
                    className={style.image}
                    src={
                        gameObject.detailImage?.[lang] ||
                        gameObject.image?.[lang] ||
                        ''
                    }
                />
            </div>
            <span className={style.cta} style={ctaStyle} aria-hidden>
                {buttonLabel}
            </span>
        </button>
    );
};
